﻿<template>
	<common-page
		:label="ts( 'openFromCloud' )" :ok-btn="ts( 'importEvent' )" :cancel-btn="ts( 'pickAnotherFile' )"
		:third-btn="ts( 'importEventOverride' )"
		:use-buttons="preview" :use-third-button="preview && duplicateEvent" @ok="proceed" @cancel="pickAnother"
		@third="proceed( true )">

		<div v-if="preview">
			<q-markup-table flat>
				<tbody>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'appVersion' ) }}</td>
					<td>{{ fileVersion }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'fileTimestamp' ) }}</td>
					<td>{{ fileTimestamp }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'csjTimestamp' ) }}</td>
					<td>{{ ts( 'whoAndWhen', { who: csjScorer, when: csjTimestamp } ) }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold">{{ ts( 'event' ) }}</td>
					<td>{{ eventDesc }}</td>
				</tr>
				<tr>
					<td class="text-left text-weight-bold vertical-top">{{ ts( 'boatClasses' ) }}</td>
					<td>
						<q-markup-table flat>
							<tbody>
							<tr v-for="( eventClass ) in eventClasses">
								<td>{{ eventClass.classDesc }}</td>
								<td class="text-right">{{ tcs( 'boats', eventClass.boats ) }}</td>
								<td class="text-right">{{ tcs( 'races', eventClass.races ) }}</td>
							</tr>
							</tbody>
						</q-markup-table>
					</td>
				</tr>
				</tbody>
			</q-markup-table>

			<div class="text-negative q-mt-lg q-mb-lg" v-if="duplicateEvent">
				<q-icon name="error" size="2em" color="negative"/>
				<span class="q-ml-sm text-weight-bold">{{ ts( 'duplicateEvent' ) }}</span><br>
			</div>
		</div>

		<q-table
			flat :rows="storedEvents" :columns="eventColumns" row-key="id" binary-state-sort
			:no-data-label="ts( 'noEvents' )" :rows-per-page-options="[ 10, 20, 30, 40, 50, 0 ]"
			:filter="cloudFilter" :filterMethod="applyCloudFilter" v-else>
			<template v-slot:header="props">
				<q-tr :props="props">
					<q-th v-for="col in props.cols" :key="col.name" :props="props" class="q-pb-xs vertical-bottom">
						<q-input
							dense class="q-mb-lg" v-model.trim="filter[col.name]"
							:label="ts( 'search', { what: col.label } )"
							@click.stop v-if="col.name === 'eventTitle' || col.name === 'author' || col.name === 'ctlId'">
							<template v-slot:append>
								<q-avatar>
									<q-icon name="search" size="2em"/>
								</q-avatar>
							</template>
						</q-input>
						<span>{{ col.label }}</span>
					</q-th>
				</q-tr>
			</template>
			<template v-slot:body-cell-actions="props">
				<q-td :props="props">
					<qe-btn
						color="primary" :label="ts( 'load' )"
						@click="openEvent( props.row.id )"/>
				</q-td>
			</template>
			<template v-slot:body-cell-title="props">
				<q-td :props="props">
					{{ props.row.eventTitle }}
					<q-icon name="sailing" class="q-ml-sm" size="1.6em" color="positive"
							:title="ts( 'finalResultsOnCloud' )" v-if="props.row.finalResults"/>
					<q-icon name="sailing" class="q-ml-sm" size="1.6em" color="info"
							:title="ts( 'provisionalResultsOnCloud' )" v-else-if="props.row.provisionalResults"/>
				</q-td>
			</template>
		</q-table>
	</common-page>
</template>

<script setup lang="ts">
import CommonPage from '@/components/parts/commonPage.vue'
import { authenticate, checkAuth, showError } from '@/network/auth'
import apiRequest from '@/network/httpClient'
import { createEventFromFile } from '@/network/importer'
import { DateFormat, ds, tcs, ts } from '@/plugins/i18nFormat'
import InfoBox from '@dialogs/InfoBox.vue'
import LogIn from '@dialogs/LogIn.vue'
import { Defaults, Options } from '@model/Setting'
import QeBtn from '@qe/qeBtn.vue'
import EventsRepo from '@repo/EventsRepo'
import SettingsRepo from '@repo/SettingsRepo'
import destr from 'destr'
import { useRepo } from 'pinia-orm'
import { Loading, useQuasar } from 'quasar'
import { computed, onMounted, ref, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const quasar = useQuasar()
const router = useRouter()
const route = useRoute()

const openFromCloud = ref( route.params.cloudId )

const storedEvents = ref( [] )
const importedContent = ref( {} )

const preview = ref( false )

const duplicateEvent = ref( false )
const fileVersion = ref( '' )
const fileTimestamp = ref( '' )
const csjTimestamp = ref( '' )
const csjScorer = ref( '' )
const eventDesc = ref( '' )
const eventClasses = ref( [] )

const isAuthenticated = computed( () => ( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) != '' ) )

const filter = ref( {} )

const cloudFilter = computed( () => {
	return getCloudFilter( filter.value )
} )

const eventColumns = [
	{
		name: 'ctlId',
		required: true,
		label: ts( 'ctl' ),
		align: 'center',
		field: row => row.ctlId,
		sortable: true,
	},
	{
		name: 'eventTitle',
		required: true,
		label: ts( 'eventTitle' ),
		align: 'left',
		field: row => row.eventTitle,
		sortable: true,
	},
	{
		name: 'author',
		required: true,
		label: ts( 'author' ),
		align: 'left',
		field: row => String( row.author.regId ).substring( 0, 4 ) + '-' + String( row.author.regId ).substring( 5 ) + ' ' + row.author.name,
		sortable: true,
	},
	{
		name: 'timestamp',
		required: true,
		label: ts( 'savedWhen' ),
		align: 'left',
		field: row => new Date( row.timestamp ).verb(),
		sortable: true,
	},
	{
		name: 'actions',
		required: true,
		label: ts( 'actions' ),
		align: 'center',
		field: () => '',
		sortable: false,
	},
]

function logIn ( attempts = 3, error = 0 ) {
	quasar.dialog( {
		component: LogIn,
		componentProps: {
			errorMsg: showError( error ),
		},
	} ).onOk( async ( payload ) => {
		const lastError = await authenticate( payload.username, payload.password )

		if ( !isAuthenticated.value ) {
			if ( attempts > 1 )
				logIn( attempts - 1, lastError )
			else
				quasar.dialog( {
					component: InfoBox,
					componentProps: {
						message: ts( 'tooManyAttempts' ),
					},
				} )
		}
		else {
			await performRefresh()
		}
	} )
}

async function performRefresh () {
	apiRequest( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) ).get( '/events/cloud/list' ).then( ( response ) => {
			if ( response.status === 200 ) {
				storedEvents.value = response.data

				if ( openFromCloud.value !== undefined ) {
					if ( storedEvents.value.some( ( ev ) => ( ev.id === Number( openFromCloud.value ) ) ) ) {
						openEvent( openFromCloud.value )
					}
				}
			}
		},
	)
}

async function refresh () {
	if ( await checkAuth() !== 'auth' ) {
		await logIn()

		return
	}

	performRefresh()
}

onMounted( async () => {
	await refresh()
} )

function fillPreview ( content ) {
	preview.value = true

	const eventFrom = content.event.from.toDate()
	const eventTo = content.event.to === null ? null : content.event.to.toDate()

	fileVersion.value = content.version
	fileTimestamp.value = ds( content.generated.toDate(), DateFormat.DateTimeHuman )
	csjTimestamp.value = ds( content.sailingDataTimestamp.toDate(), DateFormat.DateTimeHuman )
	csjScorer.value = content.authorizedScorer
	eventDesc.value = content.event.ctl + ' ' + content.event.title
		+ ' (' + ds( eventFrom, ( eventTo != null && !eventFrom.isSameAs( eventTo ) ) ? DateFormat.EventFrom : DateFormat.EventTo )
		+ ( ( eventTo == null || eventFrom.isSameAs( eventTo ) ) ? '' : ' ' + ts( 'till' ) + ' ' + ds( eventTo, DateFormat.EventTo ) ) + ')'

	eventClasses.value = []

	const boats = content.event.boats.reduce( ( acc, cur ) => acc.set( cur.id, cur.boatClassId ), new Map<string, string>( [] ) )

	const boatCountsByClass = content.event.boats.reduce( ( acc, boat ) => {
		acc[ boat.boatClassId ] = ( acc[ boat.boatClassId ] || 0 ) + 1
		return acc
	}, {} )

	const racesByClass = content.event.races.reduce( ( acc, race ) => {
		const classesInRace = new Set( race.results.map( res => boats.get( res.eventBoatId ) ) )
		classesInRace.forEach( classId => {
			if ( classId ) {
				acc[ classId ] = ( acc[ classId ] || 0 ) + 1
			}
		} )
		return acc
	}, {} )

	content.event.classes.forEach( function ( cl ) {
		eventClasses.value.push( {
			classDesc: cl.shortcut,
			weight: cl.weight,
			shortcut: cl.shortcut,
			boats: boatCountsByClass[ cl.boatClassId ] || 0,
			races: racesByClass[ cl.boatClassId ] || 0,
		} )
	} )

	eventClasses.value = eventClasses.value.sort( ( a, b ) => a.weight === b.weight ? a.shortcut.localeCompare( b.shortcut ) : b.weight - a.weight )

	duplicateEvent.value = useRepo( EventsRepo ).eventByCtlId( content.event.ctl ) != null
}

function openEvent ( eventId ) {
	apiRequest( useRepo( SettingsRepo ).getOption( Options.AuthToken, Defaults.AuthToken ) ).get( '/events/cloud/' + eventId ).then( ( response ) => {
			if ( response.status === 200 ) {
				importedContent.value = {}

				try {
					importedContent.value = destr( response.data )
				}
				catch ( e ) {
					quasar.notify( {
						icon: 'error',
						color: 'negative',
						message: ts( 'invalidFileFormat', { fileName: '' } ),
						timeout: 5000,
					} )

					return
				}

				if ( typeof importedContent.value !== 'object' || !Object.hasOwn( importedContent.value, 'software' ) || importedContent.value.software !== 'Score System' || !Object.hasOwn( importedContent.value, 'version' ) ) {
					quasar.notify( {
						icon: 'error',
						color: 'negative',
						message: ts( 'notMySaveFile', { fileName: '' } ),
						timeout: 5000,
					} )

					return
				}

				fillPreview( importedContent.value )
			}
		},
	)
}

async function proceed ( override = false ) {
	if ( override ) {
		const existingEvent = useRepo( EventsRepo ).eventByCtlId( importedContent.value.event.ctl )

		if ( existingEvent !== null )
			useRepo( EventsRepo ).destroy( existingEvent.id )
	}

	Loading.show( {
		message: ts( 'importingEvent' )
	} )

	await nextTick()
	await new Promise( resolve => setTimeout( resolve, 100 ) )

	const eventId = await createEventFromFile( importedContent.value )

	Loading.hide()

	preview.value = false
	duplicateEvent.value = false

	importedContent.value = {}

	if ( eventId !== undefined )
		router.push( { name: 'EventManager', params: { eventId: eventId } } )
}

function pickAnother () {
	preview.value = false
	duplicateEvent.value = false

	refresh()
}

function getCloudFilter ( filter ) {
	return Object.entries( filter ).map( ( entry ) => ( { column: entry[ 0 ], value: entry[ 1 ] } ) )
}

function applyCloudFilter ( rows, terms, cols, getCellValue ) {
	for ( const term of terms ) {
		if ( term.value !== '' )
			rows = rows.filter( ( row ) => ( String( getCellValue( cols
				.find( ( f ) => ( f.name === term.column ) ), row ) )
				.toSearchable().includes( String( term.value ).toSearchable() ) ) )
	}

	return rows
}
</script>
